import * as Sentry from "@sentry/react";

/**
 * Where a browser trace may be handed to the API. Relative paths cover the
 * services the app calls on its own origin (and the dev proxy); the configured
 * base URL covers builds that call the API on another host. Without this a slow
 * screen cannot be followed into the call behind it.
 */
const TRACE_PROPAGATION_TARGETS = [
  "localhost",
  /^\//,
  ...(import.meta.env.VITE_BASE_URL ? [import.meta.env.VITE_BASE_URL] : []),
];

export const initSentry = () => {
  if (import.meta.env.PROD && import.meta.env.VITE_SENTRY_DSN) {
    Sentry.init({
      dsn: import.meta.env.VITE_SENTRY_DSN,
      environment:
        import.meta.env.VITE_SENTRY_ENVIRONMENT || import.meta.env.MODE,
      // Must match the name the build uploads its source maps under, or the
      // stack traces arrive unsymbolicated.
      release: `zenith-ng@${import.meta.env.VITE_APP_VERSION || "1.0.0"}`,
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration({
          // Stated rather than assumed: this app handles payments, so every
          // replay is masked and no media is captured.
          maskAllText: true,
          maskAllInputs: true,
          blockAllMedia: true,
        }),
      ],
      // Every navigation is traced; sessions are recorded sparingly unless the
      // session hit an error, which is when a replay is worth its cost.
      tracesSampleRate: 1.0,
      replaysSessionSampleRate: 0.1,
      replaysOnErrorSampleRate: 1.0,
      // Never send IP addresses, cookies or user-agent identifiers.
      sendDefaultPii: false,
      tracePropagationTargets: TRACE_PROPAGATION_TARGETS,
      initialScope: {
        tags: {
          app: "zenith-ng",
          instance:
            import.meta.env.VITE_USER_DEPLOYMENT_INSTANCE_TYPE || "UNKNOWN",
        },
      },
    });
  }
};

export const ErrorBoundary = Sentry.ErrorBoundary;

export const captureException = Sentry.captureException;
export const captureMessage = Sentry.captureMessage;
export const setUser = Sentry.setUser;
export const withScope = Sentry.withScope;
export const setContext = Sentry.setContext;
export const setExtra = Sentry.setExtra;
export const setTag = Sentry.setTag;
export const addBreadcrumb = Sentry.addBreadcrumb;

export const configureScope = (callback: (scope: any) => void) => {
  Sentry.withScope(callback);
};
